import React, { useState } from 'react';
import PropTypes from 'prop-types';
import SidebarBody from '../SidebarBody';
import styles from './AboutLocation.module.scss';
import { HUNT_LAYER_CATEGORIES, COLORS, ESRI_FIELD_TYPES } from '../../../lib/constants';
import { stringToHash } from '../../../lib/utils';
import LayersIcon from '../../../assets/svg/LayersIcon';
import LayersOutline from '../../../assets/svg/LayersOutline';
import EyeOnIcon from '../../../assets/svg/EyeOnIcon';
import ChevronDown from '../../../assets/svg/Feather/ChevronDown';
import ChevronRight from '../../../assets/svg/Feather/ChevronRight';
import InfoButton from '../../InfoButton';
import HuntUnitsSection from './HuntUnitsSection';
import ActiveLayerSection from './ActiveLayerSection';
import AvailableLayersSection from './AvailableLayersSection';
import FeatureAtLocationCard from './FeatureAtLocationCard';
import { generateFieldsMap, formatFieldValue } from './utils';

const TOOLTIP_ACTIVE = 'active_layers';
const TOOLTIP_TEXT_ACTIVE = 'View additional location-specific information for the hunt layers you\'re currently viewing';

function OverlayAtLocation({ overlay, feature, fields }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const urlMatcher = /((http|https):\/\/)/gi;

  return (
    <div className={styles.activeLayerSection}>
      <div className={styles.activeLayerSectionHeader} onClick={() => setIsExpanded(v => !v)}>
        <div className={styles.activeLayerSectionHeaderLeft}>
          <LayersIcon color={COLORS.ghOrange} style={{ width: 18, height: 18, flexShrink: 0 }} />
          <div className={styles.activeLayerSectionTitle}>{overlay.name}</div>
        </div>
        <button className={styles.layerCardChevron}>
          {isExpanded
            ? <ChevronDown width={18} color='#ffffff' />
            : <ChevronRight width={18} color='#ffffff' />
          }
        </button>
      </div>
      {isExpanded && (
        <div className={styles.activeLayerSectionBody}>
          {Object.keys(feature.attributes).map((attrName, i) => {
            if (!feature.attributes[attrName] || feature.attributes[attrName].toString().trim() === '') return null;
            const keyAlias = fields[attrName]?.alias?.trim();
            const keyType = fields[attrName]?.type;
            if (!keyAlias) return null;

            const content = formatFieldValue(feature.attributes[attrName], keyType, keyAlias);

            return (
              <div key={i} className={styles.field}>
                <div className={styles.fieldName}>{keyAlias}</div>
                <div className={styles.fieldValue}>
                  {keyType === ESRI_FIELD_TYPES.String && urlMatcher.test(content) ? (
                    <a target="_blank" href={content} rel="noreferrer">{content.toString().trim()}</a>
                  ) : (
                    content.toString().trim()
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

OverlayAtLocation.propTypes = {
  overlay: PropTypes.object,
  feature: PropTypes.object,
  fields: PropTypes.object,
};

function LoadingCard({ text = 'Loading layers' }) {
  return (
    <div className={styles.loadingCard}>
      <div className={styles.loadingSpinner} />
      <span>{text}</span>
    </div>
  );
}

LoadingCard.propTypes = { text: PropTypes.string };

function EmptyState({ message }) {
  return <div className={styles.emptyState}>{message}</div>;
}

EmptyState.propTypes = { message: PropTypes.string };

function AboutLocationContent(props) {
  const { locationLoading, huntUnitsLoading, layersAtLocation, huntUnitsAtLocation, overlaysAtLocation, availableCategories, stateName, assetsUrl, dispatch, userProfile, featureAtLocation, hideCoords } = props;
  const [openTooltip, setOpenTooltip] = useState(null);

  const huntUnitLayers = huntUnitsAtLocation || [];
  const activeLayers = layersAtLocation.filter(
    ({ layer }) => !HUNT_LAYER_CATEGORIES.has(layer.category)
  );

  return (
    <SidebarBody className={styles.aboutLocation}>

      {/* ── Feature at Location ──────── */}
      {featureAtLocation && (
        <FeatureAtLocationCard
          feature={featureAtLocation}
          hideCoords={hideCoords}
          dispatch={dispatch}
        />
      )}

      {/* ── Hunt Units ─────────────────────────────────────── */}
      <HuntUnitsSection
        isLoading={huntUnitsLoading}
        huntUnitsAtLocation={huntUnitLayers}
      />

      {/* ── Active Layers ──────────────────────────────────── */}
      <div className={styles.sectionDivider}>
        <div className={styles.sectionDividerHeader}>
          <EyeOnIcon color='#ffffff' />
          <span>Active Layers</span>
          <InfoButton
            tooltipKey={TOOLTIP_ACTIVE}
            tooltipText={TOOLTIP_TEXT_ACTIVE}
            openTooltip={openTooltip}
            onToggle={setOpenTooltip}
          />
        </div>
        {locationLoading
          ? <LoadingCard text='Loading active layers' />
          : activeLayers.length > 0
            ? activeLayers.map((layerAtLocation, i) => (
                <ActiveLayerSection
                  key={`active-${i}-${stringToHash(JSON.stringify(layerAtLocation.layer))}`}
                  layerAtLocation={layerAtLocation}
                />
              ))
            : <EmptyState message='No layers are available for the selected area.' />
        }
      </div>

      {/* ── Overlays ───────────────────────────────────────── */}
      {overlaysAtLocation.length > 0 && (
        <div className={styles.sectionDivider}>
          <div className={styles.sectionDividerHeader}>
            <LayersOutline width={20} height={20} />
            <span>Overlays</span>
          </div>
          {overlaysAtLocation.map((overlayAtLocation, i) => {
            const { metadata, overlay } = overlayAtLocation;
            const fields = generateFieldsMap(metadata.fields);
            return metadata.features.map((feature, j) => (
              <OverlayAtLocation
                key={`${i}_${j}-${stringToHash(JSON.stringify(feature))}`}
                overlay={overlay}
                feature={feature}
                fields={fields}
              />
            ));
          })}
        </div>
      )}

      {/* ── Available Layers ───────────────────────────────── */}
      <AvailableLayersSection
        isLoading={locationLoading}
        availableCategories={availableCategories}
        stateName={stateName}
        assetsUrl={assetsUrl}
        dispatch={dispatch}
        userProfile={userProfile}
        openTooltip={openTooltip}
        onToggleTooltip={setOpenTooltip}
      />
    </SidebarBody>
  );
}

AboutLocationContent.propTypes = {
  assetsUrl: PropTypes.string,
  availableCategories: PropTypes.array,
  dispatch: PropTypes.func,
  featureAtLocation: PropTypes.object,
  hideCoords: PropTypes.bool,
  huntUnitsAtLocation: PropTypes.array,
  layersAtLocation: PropTypes.array,
  locationLoading: PropTypes.bool,
  huntUnitsLoading: PropTypes.bool,
  overlaysAtLocation: PropTypes.array,
  stateName: PropTypes.string,
  userProfile: PropTypes.object,
};

AboutLocationContent.defaultProps = {
  availableCategories: [],
  locationLoading: false,
};

export default AboutLocationContent;
